export interface ChatSummary {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  agentStarted: boolean;
}

export interface ConversationMetadata {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export async function fetchChats(): Promise<ChatSummary[]> {
  const { supabase } = await import('@upcaret/supabase-types/client');
  const { data, error } = await supabase
    .schema('api')
    .from('ai_conversations')
    .select('id, title, created_at, updated_at, metadata')
    .order('updated_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch conversations: ${error.message}`);
  }

  return (data as any[]).map((conv) => ({
    id: conv.id,
    title: conv.title,
    createdAt: conv.created_at,
    updatedAt: conv.updated_at,
    agentStarted: conv.metadata?.agent_started === true,
  }));
}

export async function createChat(
  initialQuestion: string,
): Promise<{ id: string }> {
  const { supabase } = await import('@upcaret/supabase-types/client');
  // Generate a unique ID for the conversation
  const conversationId = crypto.randomUUID();

  // Create the conversation
  const { error: convError } = await supabase
    .schema('api')
    .from('ai_conversations')
    .insert({
      id: conversationId,
      title:
        initialQuestion.length > 50
          ? initialQuestion.substring(0, 50) + '...'
          : initialQuestion,
    } as any);

  if (convError) {
    throw new Error(`Failed to create conversation: ${convError.message}`);
  }

  // Create the initial user message
  const { error: messageError } = await supabase
    .schema('api')
    .from('ai_conversation_messages')
    .insert({
      id: crypto.randomUUID(),
      conversation_id: conversationId,
      role: 'user',
      content: initialQuestion,
      model: 'claude-sonnet-5-5',
    } as any);

  if (messageError) {
    throw new Error(
      `Failed to create initial message: ${messageError.message}`,
    );
  }

  return { id: conversationId };
}

export async function fetchChatMessages(
  chatId: string,
): Promise<ChatMessage[]> {
  const { supabase } = await import('@upcaret/supabase-types/client');
  const { data, error } = await supabase
    .schema('api')
    .from('ai_conversation_messages')
    .select('id, conversation_id, role, content, created_at')
    .eq('conversation_id', chatId)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch messages: ${error.message}`);
  }

  return (data as any[]).map((msg) => ({
    id: msg.id,
    conversationId: msg.conversation_id,
    role: msg.role,
    content: msg.content,
    createdAt: msg.created_at,
  }));
}

export async function sendMessage(
  chatId: string,
  content: string,
  role: 'user' | 'assistant' = 'user',
): Promise<ChatMessage> {
  const { supabase } = await import('@upcaret/supabase-types/client');
  const messageId = crypto.randomUUID();

  const { data, error } = await supabase
    .schema('api')
    .from('ai_conversation_messages')
    .insert({
      id: messageId,
      conversation_id: chatId,
      role: role,
      content: content,
      model: 'claude-sonnet-5-5',
    } as any)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to send message: ${error.message}`);
  }

  return {
    id: (data as any).id,
    conversationId: (data as any).conversation_id,
    role: (data as any).role,
    content: (data as any).content,
    createdAt: (data as any).created_at,
  };
}

export async function fetchConversationMetadata(
  chatId: string,
): Promise<ConversationMetadata> {
  const { supabase } = await import('@upcaret/supabase-types/client');
  const { data, error } = await supabase
    .schema('api')
    .from('ai_conversations')
    .select('id, title, created_at, updated_at')
    .eq('id', chatId)
    .single();

  if (error) {
    throw new Error(`Failed to fetch conversation metadata: ${error.message}`);
  }

  return {
    id: data.id,
    title: data.title,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export async function deleteChat(chatId: string): Promise<void> {
  const { supabase } = await import('@upcaret/supabase-types/client');
  const { error } = await supabase
    .schema('api')
    .from('ai_conversations')
    .delete()
    .eq('id', chatId);

  if (error) {
    throw new Error(`Failed to delete conversation: ${error.message}`);
  }
}
